// Right-side drawer for inspecting / managing an author. Triggered
// from the Authors page (full-card click) and the Submission panel of
// ArticleReview ("Author" row chip). Shows stats, recent articles, and
// the small set of mutations the backend supports today:
//
//   • Toggle editorial_role (contributor ↔ editor) — PATCH /admin/authors/{id}
//   • Suspend / unsuspend account — POST /admin/accounts/users/{id}/{suspend|unsuspend}
//
// Renaming an author isn't wired because the PATCH endpoint only
// accepts `editorial_role` today. Add `name` server-side first if we
// want inline rename here.

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import {
  Avatar, Badge, Icon, ghostBtn, primaryBtn, KindChip, ReviewPill,
} from "./admin-ui";
import { ConfirmDialog, useToast } from "./ui-prims";
import { useAuthorActions, useAuthors, type AuthorEntry, type EditorialRole } from "@/lib/authors-hooks";
import { useAdminArticles } from "@/lib/articles-hooks";

interface Props {
  authorId: string;
  onClose: () => void;
}

export function AuthorDrawer({ authorId, onClose }: Props) {
  const { authors, refresh } = useAuthors();
  const actions = useAuthorActions();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const entry: AuthorEntry | undefined = useMemo(
    () => authors.find(a => a.id === authorId),
    [authors, authorId],
  );

  // Pull all articles and filter by this author client-side. We only
  // show the 8 most recent; full list lives on /editorial/articles.
  const { rows: allArticles } = useAdminArticles({});
  const recent = useMemo(() => {
    return allArticles
      .filter(a => (a.authorDisplay?.id ?? a.author) === authorId)
      .slice(0, 8);
  }, [allArticles, authorId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !confirmSuspend) onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, confirmSuspend]);

  if (!entry) {
    return createPortal(
      <div style={scrim} onClick={onClose}>
        <div style={panel} onClick={e => e.stopPropagation()}>
          <header style={hdr}>
            <div style={{ color: "var(--ink-3)" }}>Loading author…</div>
            <button onClick={onClose} style={ghostBtn} type="button">Close</button>
          </header>
        </div>
      </div>,
      document.body,
    );
  }

  const display = entry.display;
  const wire = entry.wire;
  const role: EditorialRole = wire.editorial_role;
  const isSuspended = wire.status === "suspended";

  const onToggleRole = async () => {
    if (busy) return;
    const next: EditorialRole = role === "editor" ? "contributor" : "editor";
    setBusy("role");
    setErr(null);
    try {
      await actions.setRole(authorId, next);
      toast(`Role set to ${next === "editor" ? "Editor" : "Contributor"}`, { tone: "success" });
      refresh();
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setErr(msg);
      toast(`Role change failed: ${msg}`, { tone: "error" });
    } finally {
      setBusy(null);
    }
  };

  const doSuspend = async () => {
    if (busy) return;
    setBusy("suspend");
    setErr(null);
    try {
      const action = isSuspended ? "unsuspend" : "suspend";
      await api.post(`/admin/accounts/users/${encodeURIComponent(authorId)}/${action}`);
      toast(isSuspended ? "Account unsuspended" : "Account suspended", { tone: "success" });
      refresh();
      setConfirmSuspend(false);
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setErr(msg);
      toast(`${isSuspended ? "Unsuspend" : "Suspend"} failed: ${msg}`, { tone: "error" });
    } finally {
      setBusy(null);
    }
  };

  return createPortal(
    <>
      <div style={scrim} onClick={onClose}>
        <div style={panel} onClick={e => e.stopPropagation()}>
          <header style={hdr}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              <Avatar initials={display.initials} tint={display.tint} size={40} title={display.name} />
              <div style={{ minWidth: 0 }}>
                <div style={kicker}>Author</div>
                <div
                  style={{
                    fontFamily: "var(--font-serif)", fontSize: 18, fontWeight: 500,
                    color: "var(--ink)", letterSpacing: "-0.015em",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}
                >
                  {display.name}
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--ink-3)",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}
                >
                  {wire.email}
                </div>
              </div>
            </div>
            <button onClick={onClose} style={ghostBtn} type="button" aria-label="Close drawer">
              {Icon.x}
            </button>
          </header>

          <div style={body}>
            {err && <div style={errBanner}>{err}</div>}

            <div style={statsGrid}>
              <Stat label="Role">
                {role === "editor" ? <Badge tone="info">Editor</Badge> : <Badge tone="ai">Contributor</Badge>}
              </Stat>
              <Stat label="Status">
                {isSuspended ? <Badge tone="neg">Suspended</Badge> : <Badge tone="pos">Active</Badge>}
              </Stat>
              <Stat label="Drafts">{entry.stats.drafts}</Stat>
              <Stat label="In review">
                <span style={{ color: entry.stats.awaiting > 0 ? "#8a5a00" : "var(--ink)" }}>
                  {entry.stats.awaiting}
                </span>
              </Stat>
              <Stat label="Published">{entry.stats.published}</Stat>
              <Stat label="Leads 30d">
                <span style={{ color: "var(--accent)" }}>{entry.stats.leads}</span>
              </Stat>
            </div>

            <Section title="Identity">
              {wire.agency && <Row label="Agency">{wire.agency}</Row>}
              {wire.cea_number && <Row label="CEA №" mono>{wire.cea_number}</Row>}
              <Row label="User ID" mono>{wire.id}</Row>
              <Row label="Last published">{entry.stats.last}</Row>
            </Section>

            <Section title="Recent articles">
              {recent.length === 0 ? (
                <div style={{ fontStyle: "italic", color: "var(--ink-3)", fontSize: 12.5, padding: "6px 0" }}>
                  Nothing yet — this author hasn't filed anything that shows in /admin/articles.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {recent.map(a => (
                    <Link
                      key={a.id}
                      to={`/editorial/articles/${a.id}`}
                      onClick={onClose}
                      style={articleRow}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                          <KindChip kind={a.kind} />
                          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--ink-4)" }}>{a.slug}</span>
                        </div>
                        <div
                          style={{
                            fontFamily: "var(--font-serif)", fontSize: 13, fontWeight: 500,
                            color: "var(--ink)", letterSpacing: "-0.005em",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}
                        >
                          {a.title}
                        </div>
                      </div>
                      <div style={{ flexShrink: 0 }}>
                        <ReviewPill state={a.state} />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </Section>

            <div style={{ display: "flex", gap: 8, marginTop: 22 }}>
              <button
                style={{ ...ghostBtn, opacity: busy ? 0.6 : 1 }}
                type="button"
                disabled={!!busy}
                onClick={onToggleRole}
                title={role === "editor" ? "Demote to contributor" : "Promote to editor"}
              >
                {role === "editor" ? "Demote to Contributor" : "Promote to Editor"}
              </button>
              <button
                style={{
                  ...primaryBtn,
                  background: isSuspended ? "var(--accent)" : "#7a2d1f",
                  opacity: busy ? 0.6 : 1,
                }}
                type="button"
                disabled={!!busy}
                onClick={() => setConfirmSuspend(true)}
              >
                {isSuspended ? "Unsuspend" : "Suspend account"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmSuspend}
        title={isSuspended ? "Unsuspend this account?" : "Suspend this account?"}
        body={
          isSuspended
            ? `${display.name} will be able to sign in and submit again.`
            : `${display.name} won't be able to sign in or file new articles until you unsuspend. Existing published articles stay live.`
        }
        confirmLabel={isSuspended ? "Unsuspend" : "Suspend"}
        confirmTone={isSuspended ? "primary" : "danger"}
        busy={busy === "suspend"}
        onConfirm={doSuspend}
        onCancel={() => setConfirmSuspend(false)}
      />
    </>,
    document.body,
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 18, padding: "12px 14px", border: "1px solid var(--rule)", borderRadius: 8, background: "var(--surface)" }}>
      <div style={kicker}>{title}</div>
      <div style={{ marginTop: 6 }}>{children}</div>
    </div>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={kicker}>{label}</div>
      <div style={{ marginTop: 4, fontFamily: "var(--font-serif)", fontSize: 17, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.015em", lineHeight: 1 }}>
        {children}
      </div>
    </div>
  );
}

function Row({ label, children, mono }: { label: string; children: React.ReactNode; mono?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0", borderTop: "1px solid var(--rule)", fontFamily: "var(--font-ui)", fontSize: 12.5 }}>
      <span style={{ color: "var(--ink-3)" }}>{label}</span>
      <span style={{ color: "var(--ink-2)", textAlign: "right", fontFamily: mono ? "var(--font-mono)" : "inherit", fontSize: mono ? 11.5 : 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {children}
      </span>
    </div>
  );
}

const scrim: CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(23,32,29,0.32)",
  zIndex: 90, display: "flex", justifyContent: "flex-end",
};
const panel: CSSProperties = {
  width: "min(580px, 92vw)", height: "100%", background: "var(--bg)",
  display: "flex", flexDirection: "column",
  boxShadow: "-8px 0 24px rgba(23,32,29,0.10)",
};
const hdr: CSSProperties = {
  padding: "14px 20px", borderBottom: "1px solid var(--rule)",
  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
  background: "var(--surface)",
};
const body: CSSProperties = {
  padding: 20, overflowY: "auto", flex: 1, minHeight: 0,
};
const kicker: CSSProperties = {
  fontSize: 9.5, color: "var(--ink-4)", fontWeight: 600,
  letterSpacing: "0.12em", textTransform: "uppercase",
  fontFamily: "var(--font-ui)",
};
const statsGrid: CSSProperties = {
  display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14,
  padding: "14px 16px", border: "1px solid var(--rule)", borderRadius: 8,
  background: "var(--surface)",
};
const articleRow: CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
  padding: "10px 4px", borderTop: "1px solid var(--rule)",
  textDecoration: "none", color: "inherit",
};
const errBanner: CSSProperties = {
  background: "rgba(122,45,31,0.08)", color: "#7a2d1f",
  padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: 13,
};
